import { useEffect, useState } from 'react';
import { usePortal } from '@/contexts/PortalContext';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Clock, Circle } from 'lucide-react';

export default function PortalProgress() {
  const { api, user } = usePortal();
  const [data, setData] = useState(null);
  const pid = user?.projects?.[0];

  useEffect(() => { if (pid) api(`/projects/${pid}/progress`).then(r => setData(r.data)).catch(() => {}); }, [api, pid]);

  if (!data) return <div className="h-64 bg-white rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="portal-progress">
      <div>
        <h1 className="text-2xl font-extrabold text-[#1e3a50]">Project Progress</h1>
        <p className="text-sm font-medium text-[#6b8a9e]">{data.project_name} — {data.progress_pct}% complete</p>
      </div>

      <div className="space-y-0">
        {(data.stages || []).map((stage, idx) => {
          const isComplete = stage.status === 'completed';
          const isActive = stage.status === 'in_progress';
          return (
            <div key={idx} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isComplete ? 'bg-emerald-500' : isActive ? 'bg-[#4a9ac5] ring-4 ring-[#4a9ac5]/20' : 'bg-gray-200'
                }`}>
                  {isComplete ? <CheckCircle className="w-5 h-5 text-white" /> :
                   isActive ? <Clock className="w-5 h-5 text-white" /> :
                   <Circle className="w-5 h-5 text-gray-400" />}
                </div>
                {idx < data.stages.length - 1 && (
                  <div className={`w-0.5 flex-1 min-h-[40px] ${isComplete ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                )}
              </div>
              <Card className={`flex-1 mb-3 border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${isActive ? 'ring-1 ring-[#4a9ac5]/30' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-bold ${isComplete ? 'text-emerald-700' : isActive ? 'text-[#2a6496]' : 'text-gray-400'}`}>{stage.name}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      isComplete ? 'bg-emerald-50 text-emerald-700' : isActive ? 'bg-[#4a9ac5]/10 text-[#2a6496]' : 'bg-gray-100 text-gray-400'
                    }`}>{isComplete ? 'Complete' : isActive ? 'In Progress' : 'Upcoming'}</span>
                  </div>
                  {stage.note && <p className="text-xs font-medium text-[#4a6a7e] mt-1">{stage.note}</p>}
                  {isComplete && stage.completed_at && <p className="text-[9px] text-[#8faab8] mt-1">Completed {new Date(stage.completed_at).toLocaleDateString()}</p>}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
      {data.target_end_date && <p className="text-xs font-medium text-[#6b8a9e] text-center">Estimated completion: <span className="font-bold">{data.target_end_date}</span></p>}
    </div>
  );
}
